/*
 * WAIVER SIGNING SNAPSHOT — what a signed copy may render from (design
 * DESIGN-MY-WAIVER-SIGNING-SNAPSHOT-V1, boardroom-accepted Tier B; FE flag
 * VITE_WAIVER_SNAPSHOT_RECEIPT_ENABLED). Pure + unit-tested.
 *
 * The rule: a signed copy renders ONLY from the server-held record —
 *   - a stored signing snapshot → that snapshot's text, participant, banner
 *     and logo, never the current template or branding;
 *   - a legacy record (no snapshot) whose CURRENT template hashes to the
 *     signed hash → the current text, labelled "reconstructed";
 *   - a legacy record whose text changed → REFUSED ("The waiver text has
 *     changed since you signed; contact the organiser."). Never new text
 *     beside an old signature;
 *   - settings unreachable / record unreadable → unavailable (retry), never a
 *     fallback to the current text.
 */
import type { WaiverRecordSnapshotResponse } from '../../data/myApi'
import { canonicalTemplateText, hashWaiverTemplate, type WaiverTemplate } from '../../data/waiverTemplate'
import { formatStamp } from './waiverModel'

/** The receipt/print record shape (waiverRecordStore WaiverRecord fields). */
export interface SignedReceipt {
  participant: string
  guardian: boolean
  signedBy: string
  when: string
  docs: Array<{ title: string; version: string }>
  declined: string[]
}

export type ServerRecordState =
  | { status: 'off' }
  | { status: 'loading' }
  | { status: 'none' }
  | { status: 'error' }
  | { status: 'ready'; record: WaiverRecordSnapshotResponse }

export type SignedCopySource =
  | {
      kind: 'snapshot'
      template: WaiverTemplate
      /** The participant value the snapshot fills ({{participant_name}}). */
      who: string
      bannerUrl?: string
      logoUrl: string
      receipt: SignedReceipt
    }
  | { kind: 'reconstructed'; template: WaiverTemplate; templateBannerUrl: string | null; receipt: SignedReceipt }
  | { kind: 'refused'; receipt: SignedReceipt }
  | { kind: 'unavailable'; receipt: SignedReceipt | null }
  | { kind: 'loading' }

/** The FE's own pin recipe is an 8-hex FNV; a 64-hex hash is a guardian
 *  row's terms SHA-256 (server-verified only). */
const isFnvPin = (h: string) => /^[0-9a-f]{8}$/.test(h)

function toTemplate(display: NonNullable<WaiverRecordSnapshotResponse['snapshot']>['template']['display']): WaiverTemplate {
  const t: WaiverTemplate = { organiser: display.organiser, event: display.event, documents: display.documents }
  if (display.guardianDeclaration) t.guardianDeclaration = display.guardianDeclaration
  return t
}

export function receiptFromServer(rec: WaiverRecordSnapshotResponse, fallbackWho: string): SignedReceipt {
  const snap = rec.snapshot
  const guardian = snap ? snap.signature.relationship === 'guardian' : rec.signerRelationship === 'guardian' || rec.guardianSigned
  const docs = rec.documents && rec.documents.length > 0
    ? rec.documents
    : snap
      ? snap.template.display.documents.map((d) => ({ title: d.title, version: d.version }))
      : []
  const at = new Date(rec.acceptedAt)
  return {
    participant: snap ? (snap.participant.value ?? '') : (rec.onBehalfOfName ?? fallbackWho),
    guardian,
    signedBy: rec.signerName,
    when: Number.isNaN(at.getTime()) ? rec.acceptedAt : formatStamp(at),
    docs,
    declined: snap ? snap.signature.declined_sections : (rec.declinedSections ?? []),
  }
}

export function signedCopySource(state: ServerRecordState, fallbackWho: string): SignedCopySource {
  if (state.status === 'loading' || state.status === 'off') return { kind: 'loading' }
  if (state.status !== 'ready') return { kind: 'unavailable', receipt: null }
  const rec = state.record
  const receipt = receiptFromServer(rec, fallbackWho)
  if (rec.snapshot) {
    const banner = rec.snapshot.branding.banner_url
    return {
      kind: 'snapshot',
      template: toTemplate(rec.snapshot.template.display),
      who: rec.snapshot.participant.value ?? '',
      ...(banner ? { bannerUrl: banner } : {}),
      logoUrl: rec.snapshot.branding.logo_url,
      receipt,
    }
  }
  const r = rec.reconstruction
  if (!r || r.status === 'unavailable') return { kind: 'unavailable', receipt }
  if (r.status === 'changed' || !r.template) return { kind: 'refused', receipt }
  const template = toTemplate(r.template)
  // Belt and braces (design §9): for a row the FE pinned, its own recipe
  // must agree with the server's verdict before any current text is shown.
  if (isFnvPin(rec.templateHash) && hashWaiverTemplate(canonicalTemplateText(template)) !== rec.templateHash) {
    return { kind: 'refused', receipt }
  }
  return { kind: 'reconstructed', template, templateBannerUrl: r.bannerUrl ?? null, receipt }
}
